import React, { useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ApiProvider } from '../../state/ApiContext';
import { ThemeProvider } from '../../state/ThemeContext';
import ChartView from './ChartView';

/**
 * Chart embeds for markdown surfaces (wiki pages, card descriptions).
 *
 * Markdown is rendered to an HTML STRING and sanitized by DOMPurify, so a
 * React component can't be emitted directly. Instead a text transform turns
 * `![label](chart:path)` into a placeholder `<div data-chart-path="…">`
 * (div + data-* survive DOMPurify's default allow-list), and a hook hydrates
 * every placeholder inside the rendered container into a live ChartView.
 */

const CHART_EMBED_CLASS = 'chart-embed-placeholder';

/**
 * Text transform: `![label](chart:target)` → placeholder div. `target` is a
 * databases/-relative chart path; the `.chart.json` suffix may be omitted.
 * Code fences and inline code are left untouched so examples render as text.
 */
export function rewriteChartEmbeds(markdown: string): string {
  return markdown.replace(
    /```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`|!\[([^\]]*)\]\(chart:([^)\s]+)\)/g,
    (token, _label?: string, target?: string) => {
      if (target === undefined) return token; // inside code: leave as-is
      let path = target.trim();
      if (!path) return token;
      if (!path.toLowerCase().endsWith('.chart.json')) {
        path = `${path}.chart.json`;
      }
      return `<div class="${CHART_EMBED_CLASS}" data-chart-path="${escapeAttr(
        path,
      )}"></div>`;
    },
  );
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Hydrate chart placeholders inside a rendered-markdown container. Pass the
 * container's ref and the rendered HTML string (the dependency that signals
 * the container's content was replaced). Each placeholder gets its own React
 * root, torn down asynchronously on re-render/unmount (React forbids
 * synchronous unmounts from effects).
 */
export function useChartEmbeds(
  containerRef: React.RefObject<HTMLElement | null>,
  renderedHtml: string,
  teamId: number,
  projectId: number,
): void {
  const rootsRef = useRef<Root[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const placeholders = Array.from(
      container.querySelectorAll<HTMLElement>(`.${CHART_EMBED_CLASS}`),
    );
    for (const el of placeholders) {
      const path = el.getAttribute('data-chart-path');
      if (!path || el.childNodes.length > 0) continue;
      const root = createRoot(el);
      rootsRef.current.push(root);
      root.render(
        <ApiProvider>
          <ThemeProvider>
            <ChartView teamId={teamId} projectId={projectId} path={path} />
          </ThemeProvider>
        </ApiProvider>,
      );
    }
    return () => {
      const roots = rootsRef.current;
      rootsRef.current = [];
      // Defer: unmounting synchronously during a render pass is an error.
      setTimeout(() => roots.forEach((root) => root.unmount()), 0);
    };
  }, [containerRef, renderedHtml, teamId, projectId]);
}
